import React, { useState } from 'react';
import { 
  LockClosedIcon, 
  ShieldCheckIcon, 
  KeyIcon, 
  ServerIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import Sidebar from '../../components/navigation/Sidebar';
import TopBar from '../../components/navigation/TopBar';
import QuickLinks from '../../components/navigation/QuickLinks';

interface MessageEncryptionConfig {
  algorithm: string;
  keyRotation: string;
  encryptAtRest: boolean;
  encryptInTransit: boolean;
}

interface StorageEncryptionConfig {
  databaseEncryption: boolean;
  fileEncryption: boolean;
  backupEncryption: boolean;
  keyManagement: string;
}

export const EncryptionConfigPage: React.FC = () => {
  const [messageEncryption, setMessageEncryption] = useState<MessageEncryptionConfig>({
    algorithm: 'AES-256',
    keyRotation: '30',
    encryptAtRest: true,
    encryptInTransit: true
  });

  const [storageEncryption, setStorageEncryption] = useState<StorageEncryptionConfig>({
    databaseEncryption: true,
    fileEncryption: true,
    backupEncryption: true,
    keyManagement: 'automatic'
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <LockClosedIcon className="w-8 h-8 text-[#FF5722] mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Encryption Configuration</h1>
        </div>

        {/* Message Encryption Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <KeyIcon className="w-6 h-6 text-[#FF5722] mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Message Encryption</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Encryption Algorithm</h3>
                <select
                  value={messageEncryption.algorithm}
                  onChange={(e) => setMessageEncryption({...messageEncryption, algorithm: e.target.value})}
                  className="w-full border rounded-md p-2"
                >
                  <option value="AES-256">AES-256</option>
                  <option value="AES-128">AES-128</option>
                  <option value="ChaCha20">ChaCha20</option>
                </select>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Key Rotation Period (Days)</h3>
                <input
                  type="number"
                  value={messageEncryption.keyRotation}
                  onChange={(e) => setMessageEncryption({...messageEncryption, keyRotation: e.target.value})}
                  className="w-full border rounded-md p-2"
                  min="1"
                />
              </div>
            </div>
            <div className="flex space-x-4">
              <label className="flex items-center p-4 bg-gray-50 rounded-lg flex-1">
                <input
                  type="checkbox"
                  checked={messageEncryption.encryptAtRest}
                  onChange={(e) => setMessageEncryption({...messageEncryption, encryptAtRest: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Encrypt at Rest</span>
              </label>
              <label className="flex items-center p-4 bg-gray-50 rounded-lg flex-1">
                <input
                  type="checkbox"
                  checked={messageEncryption.encryptInTransit}
                  onChange={(e) => setMessageEncryption({...messageEncryption, encryptInTransit: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Encrypt in Transit</span>
              </label>
            </div>
          </div>
        </div>

        {/* Storage Encryption Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <ServerIcon className="w-6 h-6 text-[#FF5722] mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Storage Encryption</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={storageEncryption.databaseEncryption}
                  onChange={(e) => setStorageEncryption({...storageEncryption, databaseEncryption: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Database Encryption</span>
              </label>
              <label className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={storageEncryption.fileEncryption}
                  onChange={(e) => setStorageEncryption({...storageEncryption, fileEncryption: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
                <span className="ml-2 text-sm text-gray-700">File Encryption</span>
              </label>
              <label className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={storageEncryption.backupEncryption}
                  onChange={(e) => setStorageEncryption({...storageEncryption, backupEncryption: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Backup Encryption</span>
              </label>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Key Management</h3>
              <select
                value={storageEncryption.keyManagement}
                onChange={(e) => setStorageEncryption({...storageEncryption, keyManagement: e.target.value})}
                className="w-full border rounded-md p-2"
              >
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E]">
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default EncryptionConfigPage; 